import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {Box, Button, CircularProgress} from '@mui/material'
import {useFormik} from 'formik'
import FormikSelect from 'shared/FormikSelect'
import FormikTextField from 'shared/FormikTextField'
import {type RootState, type AppDispatch} from 'redux/store'
import {fetchCarrierList} from 'redux/common.slice'
import {PAGE_NAME} from 'config/constant'
import {fetchFlightStatus} from 'features/HomePage/FlightStatus/flightstatus.slice'
import {MAX_DATE_BY_CARRIER} from 'features/HomePage/FlightStatus/flightstatus.constant'
import 'features/HomePage/FlightStatus/index.scss'
import {
    flightNoRegex,
    formatFlightNumber,
    viewFlightStatusFormValidations
} from 'features/HomePage/FlightStatus/flightstatus.utils'
import moment from 'moment'
import FormikDatePicker from 'shared/FormikDatePicker'
import _ from 'lodash'
import {toggleLoginPopup, navigatePath, viewScheduleValues} from 'features/Login/login.slice'

const Form = (props: any) => {
    const {navigateURL, mainPage} = props
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [carrierOptions, setCarrierOptions] = useState([])
    const [maxDays, setMaxDays] = useState(30)
    const [isDisabled, setDisabled] = useState(true)
    const carrierList = useSelector((state: any) => state.common.carrier)
    const securityParams = useSelector((state: RootState) => state.login.loginDetails?.securityParams)
    const isLoggedIn = useSelector((state: any) => state?.login?.isLoggedIn)
    const state = useSelector((state: any) => state.flightStatus)
    const defaultValues = useSelector((state: any) => state.flightStatus.initialValues)
    let initialValues = {
        carrier: '-Select-',
        flightDate: new Date(),
        flightNo: ''
    }
    if (!mainPage) {
        initialValues = {...defaultValues}
    }

    const formik = useFormik({
        initialValues,
        validationSchema: viewFlightStatusFormValidations,
        onSubmit: async (values) => {
            const carrier = values.carrier
            const flightNo = values.flightNo
            if (flightNoRegex.test(flightNo) && carrier !== '-Select-') {
                const formattedFlightNo = formatFlightNumber(carrier, flightNo)
                await formik.setFieldValue('flightNo', formattedFlightNo)
                values.flightNo = formattedFlightNo
            }
            if (isLoggedIn) {
                dispatch(fetchFlightStatus({...values, userId: _.get(securityParams, 'userID', 'CPSGUEST')}))
                    .then((resp) => {
                        navigate(navigateURL ?? '')
                    })
                    .catch(() => {})
            } else {
                dispatch(toggleLoginPopup(true))
                dispatch(navigatePath(navigateURL))
                dispatch(viewScheduleValues(values))
            }
        }
    })

    useEffect(() => {
        void dispatch(
            fetchCarrierList({
                pageName: PAGE_NAME[33]
            })
        )
    }, [])

    useEffect(() => {
        const list = carrierList?.map((temp) => ({
            value: `${temp.code} - ${temp.displayName} (${temp.name})`,
            label: `${temp.displayName} (${temp.name})`
        }))
        setCarrierOptions(list)
    }, [carrierList])

    useEffect(() => {
        if (formik.values.carrier !== '-Select-' && formik.values.flightNo !== '' && formik.isValid) {
            setDisabled(false)
        } else {
            setDisabled(true)
        }
    }, [formik])

    useEffect(() => {
        const matches = formik.values.carrier?.match(/\((.*?)\)/)
        let carrier = ''
        if (matches && matches.length >= 2) {
            carrier = matches[1]
        }
        const maxDays = MAX_DATE_BY_CARRIER[carrier] || 30
        setMaxDays(maxDays)
        const date = formik.values.flightDate
        if (date.getTime() > moment().add(parseInt(maxDays), 'days').toDate().getTime()) {
            void formik.setFieldValue('flightDate', new Date())
        }
    }, [formik.values.carrier])

    const hadleFlightNoChange = async (e) => {
        const flightNo = e.target.value
        await formik.setFieldValue('flightNo', flightNo.toUpperCase().replace(/[^a-zA-Z0-9]/g, ''))
    }
    return (
        <form className='form-container' onSubmit={formik.handleSubmit}>
            <Box className='form-input-container'>
                <label htmlFor='carrier'>
                    Carrier<span>&#42;</span>
                </label>
                <FormikSelect
                    id='carrier'
                    formik={formik}
                    options={carrierOptions}
                    className='formik-input__select'
                    placeholder='- Select Carrier -'
                    autoFocus
                    disabled={state.isLoading}
                />
            </Box>
            <Box className='form-input-container'>
                <label htmlFor='flightDate'>
                    Flight Date<span>&#42;</span>
                </label>
                <FormikDatePicker
                    id='flightDate'
                    formik={formik}
                    className='formik-date'
                    disabled={state.isLoading}
                    maxDays={maxDays}
                />
            </Box>
            <Box className='form-input-container'>
                <label htmlFor='flightNo'>
                    Flight No.<span>&#42;</span>
                </label>
                <FormikTextField
                    id='flightNo'
                    formik={formik}
                    className='formik-input__textfield'
                    onChange={hadleFlightNoChange}
                    disabled={state.isLoading}
                    maxLength={8}
                />
            </Box>
            <Button type='submit' variant='contained' className='btn-primary' disabled={isDisabled || state.isLoading}>
                {state.isLoading ? (
                    <CircularProgress size={24} className='flightstatus__submit-btn-progress' />
                ) : (
                    'View status'
                )}
            </Button>
        </form>
    )
}

export default Form
